import React from 'react';
import { schemaLabel } from '../utils/schemaUtils';

const TYPE_HINTS = {
  nodes: 'User picks nodes (set min/max to 1 for single node, or use min/max for multi-select)',
  tag: 'User picks one of their tags',
  schema: 'User picks a schema type',
  text: 'Free-form text input',
  number: 'Numeric value',
  boolean: 'On/off toggle',
  select: 'Choose from predefined options',
  file: 'Upload a file',
  timestamp: 'User picks a date/time',
};

const inputStyle = {
  padding: '8px',
  borderRadius: '6px',
  border: '1px solid var(--border-color)',
  fontSize: '13px',
  background: 'var(--bg-primary)',
  color: 'var(--text-primary)',
};

const labelStyle = {
  display: 'block',
  fontSize: '11px',
  color: 'var(--text-secondary)',
  marginBottom: '4px',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
};

/**
 * InputParametersBuilder
 *
 * Two modes:
 *   - Edit mode (default): developer defines parameter definitions (key, label, type, options)
 *   - readOnly mode: displays parameter definitions as read-only cards
 *
 * For collecting VALUES from end-users at runtime, use the `values` + `onChangeValues` props.
 * When these are provided, each parameter renders an interactive input for the user to fill in.
 */
function InputParametersBuilder({ items, onChange, readOnly = false, values, onChangeValues, files, onChangeFiles, schemas = [] }) {
  const isValueMode = values !== undefined && onChangeValues !== undefined;

  const updateItem = (idx, changes) => {
    if (readOnly) return;
    const updated = [...items];
    updated[idx] = { ...updated[idx], ...changes };
    onChange(updated);
  };

  const removeItem = (idx) => {
    if (readOnly) return;
    onChange(items.filter((_, i) => i !== idx));
  };

  const addItem = () => {
    if (readOnly) return;
    onChange([...items, { key: '', label: '', type: 'text' }]);
  };

  // Value collection mode: render interactive form for end-users
  if (isValueMode) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {items.map((param) => (
          <div key={param.key}>
            {param.type === 'boolean' ? (
              <label style={{
                display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer',
                padding: '10px 14px', background: 'var(--bg-secondary)', borderRadius: '8px',
                border: '1px solid var(--border-color)'
              }}>
                <input
                  type="checkbox"
                  checked={values[param.key] ?? param.default ?? false}
                  onChange={(e) => onChangeValues({ ...values, [param.key]: e.target.checked })}
                  style={{ width: '18px', height: '18px', accentColor: '#4f46e5' }}
                />
                <span style={{ fontSize: '14px', color: 'var(--text-primary)', fontWeight: '500' }}>{param.label || param.key}</span>
              </label>
            ) : param.type === 'select' ? (
              <div>
                <label style={{ ...labelStyle, fontSize: '13px', fontWeight: '500', color: 'var(--text-primary)' }}>
                  {param.label || param.key}
                </label>
                <select
                  value={values[param.key] ?? param.default ?? ''}
                  onChange={(e) => onChangeValues({ ...values, [param.key]: e.target.value })}
                  style={{ ...inputStyle, width: '100%' }}
                >
                  <option value="">Select...</option>
                  {(param.options || []).map((opt) => {
                    const val = typeof opt === 'object' ? opt.value : opt;
                    const label = typeof opt === 'object' ? opt.label : opt;
                    return <option key={val} value={val}>{label}</option>;
                  })}
                </select>
              </div>
            ) : param.type === 'file' ? (
              <div>
                <label style={{ ...labelStyle, fontSize: '13px', fontWeight: '500', color: 'var(--text-primary)' }}>
                  {param.label || param.key}
                </label>
                {files && files[param.key] ? (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '10px 14px', background: 'var(--bg-secondary)', borderRadius: '8px',
                    border: '1px solid var(--border-color)'
                  }}>
                    <span style={{ fontSize: '14px', color: 'var(--text-primary)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {param.multiple && Array.isArray(files[param.key])
                        ? files[param.key].map(f => f.name).join(', ')
                        : files[param.key].name}
                    </span>
                    <button
                      type="button"
                      onClick={() => onChangeFiles && onChangeFiles({ ...files, [param.key]: undefined })}
                      style={{
                        padding: '4px 10px', borderRadius: '6px', border: '1px solid rgba(239, 68, 68, 0.3)',
                        background: 'transparent', color: '#ef4444', cursor: 'pointer', fontSize: '13px', flexShrink: 0
                      }}
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <input
                    type="file"
                    accept={param.accept || '*'}
                    multiple={!!param.multiple}
                    onChange={(e) => {
                      if (!onChangeFiles) return;
                      const selected = param.multiple ? Array.from(e.target.files) : e.target.files[0];
                      if (selected) onChangeFiles({ ...files, [param.key]: selected });
                    }}
                    style={{ ...inputStyle, width: '100%', padding: '8px' }}
                  />
                )}
              </div>
            ) : (
              <div>
                <label style={{ ...labelStyle, fontSize: '13px', fontWeight: '500', color: 'var(--text-primary)' }}>
                  {param.label || param.key}
                </label>
                <input
                  type={param.type === 'number' ? 'number' : 'text'}
                  value={values[param.key] ?? param.default ?? ''}
                  onChange={(e) => onChangeValues({
                    ...values,
                    [param.key]: param.type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value
                  })}
                  placeholder={param.label || param.key}
                  style={{ ...inputStyle, width: '100%' }}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }

  // Read-only mode: display parameter definitions
  if (readOnly) {
    return (
      <div>
        {items.map((param, idx) => (
          <div key={idx} style={{
            display: 'flex', gap: '12px', alignItems: 'center', padding: '10px 14px',
            background: 'var(--bg-secondary)', border: '1px solid var(--border-color)',
            borderRadius: '8px', marginBottom: '8px'
          }}>
            <code style={{ fontSize: '12px', color: 'var(--text-secondary)', fontFamily: 'monospace', minWidth: '80px' }}>
              {param.key || '?'}
            </code>
            <span style={{ flex: 1, fontSize: '14px', fontWeight: '500', color: 'var(--text-primary)' }}>
              {param.label || param.key}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
              <span style={{
                fontSize: '11px', padding: '3px 8px', borderRadius: '4px', fontWeight: '600',
                background: 'var(--bg-tertiary)', color: 'var(--text-secondary)', textTransform: 'uppercase'
              }}>
                {param.type}
              </span>
              {TYPE_HINTS[param.type] && (
                <span style={{ fontSize: '11px', color: 'var(--text-tertiary, #999)' }}>
                  {TYPE_HINTS[param.type]}
                </span>
              )}
            </span>
            {param.type === 'select' && param.options?.length > 0 && (
              <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                [{param.options.join(', ')}]
              </span>
            )}
            {param.type === 'file' && param.accept && (
              <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                {param.accept}
              </span>
            )}
            {param.type === 'nodes' && param.schema && (
              <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontFamily: 'monospace' }}>
                {param.schema}
              </span>
            )}
            {param.type === 'nodes' && (param.min !== undefined || param.max !== undefined) && (
              <span style={{ fontSize: '11px', color: 'var(--text-tertiary, #999)' }}>
                {param.min !== undefined ? `min: ${param.min}` : ''}{param.min !== undefined && param.max !== undefined ? ', ' : ''}{param.max !== undefined ? `max: ${param.max}` : ''}
              </span>
            )}
            {param.required && (
              <span style={{ fontSize: '11px', color: '#ef4444', fontWeight: 600 }}>required</span>
            )}
          </div>
        ))}
      </div>
    );
  }

  // Edit mode: developer defines parameter structure
  return (
    <div>
      {items.map((param, idx) => (
        <div key={idx} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'start', flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="key"
            value={param.key || ''}
            onChange={(e) => updateItem(idx, { key: e.target.value })}
            style={{ ...inputStyle, width: '100px', fontFamily: 'monospace' }}
          />
          <input
            type="text"
            placeholder="Label"
            value={param.label || ''}
            onChange={(e) => updateItem(idx, { label: e.target.value })}
            style={{ ...inputStyle, flex: 1, minWidth: '120px' }}
          />
          <select
            value={param.type || 'text'}
            onChange={(e) => updateItem(idx, { type: e.target.value })}
            style={inputStyle}
          >
            <option value="nodes">Nodes</option>
            <option value="tag">Tag</option>
            <option value="schema">Schema</option>
            <option value="text">Text</option>
            <option value="number">Number</option>
            <option value="boolean">Boolean</option>
            <option value="select">Select</option>
            <option value="file">File</option>
            <option value="timestamp">Timestamp</option>
          </select>
          {param.type === 'file' && (
            <input
              type="text"
              placeholder="accept (e.g. .csv,.xlsx)"
              value={param.accept || ''}
              onChange={(e) => updateItem(idx, { accept: e.target.value })}
              style={{ ...inputStyle, flex: 1, minWidth: '120px' }}
            />
          )}
          {param.type === 'select' && (
            <input
              type="text"
              placeholder="option1, option2, ..."
              value={Array.isArray(param.options) ? param.options.join(', ') : ''}
              onChange={(e) => updateItem(idx, { options: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
              style={{ ...inputStyle, flex: 1, minWidth: '120px' }}
            />
          )}
          {param.type === 'nodes' && (
            schemas.length > 0 ? (
              <select
                value={param.schema || ''}
                onChange={(e) => updateItem(idx, { schema: e.target.value || undefined })}
                style={{ ...inputStyle, flex: 1, minWidth: '140px' }}
              >
                <option value="">Any schema</option>
                {schemas.map(s => {
                  const uri = s.schema_name || s.uri;
                  return <option key={s.id || uri} value={uri}>{schemaLabel(uri) || uri}</option>;
                })}
              </select>
            ) : (
              <input
                type="text"
                placeholder="schema URI (e.g. raw:diary)"
                value={param.schema || ''}
                onChange={(e) => updateItem(idx, { schema: e.target.value })}
                style={{ ...inputStyle, flex: 1, minWidth: '140px' }}
              />
            )
          )}
          {param.type === 'nodes' && (
            <>
              <input
                type="number"
                placeholder="min"
                value={param.min ?? ''}
                onChange={(e) => updateItem(idx, { min: e.target.value === '' ? undefined : Number(e.target.value) })}
                style={{ ...inputStyle, width: '60px' }}
                min={0}
              />
              <input
                type="number"
                placeholder="max"
                value={param.max ?? ''}
                onChange={(e) => updateItem(idx, { max: e.target.value === '' ? undefined : Number(e.target.value) })}
                style={{ ...inputStyle, width: '60px' }}
                min={0}
              />
            </>
          )}
          {param.type === 'nodes' && (
            <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: 'var(--text-secondary)', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={param.required ?? false}
                onChange={(e) => updateItem(idx, { required: e.target.checked })}
              />
              Required
            </label>
          )}
          <button
            type="button"
            onClick={() => removeItem(idx)}
            style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid rgba(239, 68, 68, 0.3)', background: 'transparent', color: '#ef4444', cursor: 'pointer', fontSize: '16px' }}
          >
            &times;
          </button>
          {TYPE_HINTS[param.type || 'text'] && (
            <div style={{ width: '100%', fontSize: '11px', color: 'var(--text-tertiary, #999)', marginTop: '-2px', paddingLeft: '2px' }}>
              {TYPE_HINTS[param.type || 'text']}
            </div>
          )}
        </div>
      ))}
      <button
        type="button"
        onClick={addItem}
        style={{ padding: '6px 14px', borderRadius: '6px', border: '1px dashed var(--border-color)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '13px' }}
      >
        + Add Variable
      </button>
    </div>
  );
}

export default InputParametersBuilder;
